import { useMemo } from 'react';
import { Layers, Receipt, TrendingUp, Truck, Users } from 'lucide-react';
import { CategoryDonut, DailyTrendChart, RankedBarChart } from '../charts/Charts';
import { ChartCard, StatTile } from './StatsParts';
import { Spinner } from '../ui/Spinner';
import { LoadError } from '../common/LoadError';
import { useExpenses } from '../../hooks/useExpenses';
import { useFinanceSummary } from '../../hooks/useFinanceSummary';
import { aISODate } from '../../utils/finance';
import { hayFinanzas } from '../../utils/features';
import { useAuth } from '../../context/AuthContext';
import type { FinanceSummary } from '../../types/finance';
import type { Expense } from '../../types/expense';
import { formatearCOP } from '../../utils/expenses';
import {
  dailyAmounts, expensesByCategory, expensesByDriver, expensesByVehicle, resolveRange, summarizeExpenses,
} from '../../utils/dashboardStats';

interface ExpensesStatsPanelProps {
  from?: string;
  to?: string;
  periodLabel: string;
}

// Los importes son texto (`Money`): el gráfico recibe un `number` solo para
// dibujar la barra, y lo que se LEE — total, promedio, tooltip — sale
// formateado desde ahí, nunca de una suma en float64.
const pesos = (value: number) => formatearCOP(String(value));

// El centro de la dona mide ~110px: el importe exacto no cabe (`$ 6.459.567,89`
// se sale del aro). Ahí va la forma corta; el exacto está en el tile de arriba.
// Se arma a mano porque `notation: 'compact'` cambia de sufijo según el motor
// de ICU ("K" en Node, "mil" en el navegador).
const decimal = new Intl.NumberFormat('es-CO', { maximumFractionDigits: 1 });
function pesosCompactos(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `$ ${decimal.format(value / 1_000_000)} M`;
  if (Math.abs(value) >= 1_000) return `$ ${decimal.format(value / 1_000)} mil`;
  return pesos(value);
}

/**
 * Gastos del periodo. Se monta solo con la pestaña activa (ver
 * `LogbookStatsPanel`). El filtro va al servidor por `incurredAt`: el mes al
 * que pertenece un peaje es el de cuando se pagó, no el de cuando se digitó.
 */
export function ExpensesStatsPanel({ from, to, periodLabel }: ExpensesStatsPanelProps) {
  const { expenses, isLoading, error } = useExpenses({ from, to });
  // FE-32: el total y el reparto por categoría se leen de los AGREGADOS del
  // servidor (`/finance/summary`, ADR-016), no se suman aquí. El resto de los
  // gráficos —tendencia diaria, ranking por vehículo y conductor— sigue sobre
  // el detalle, porque el agregado no tiene ese grano.
  //
  // FE-35 (ADR-034): `/finance/summary` pertenece al módulo de finanzas. Sin
  // él no se llama (respondería 403 MODULE_DISABLED) y el panel se queda con
  // el cálculo local, que es exactamente lo que tenía antes de FE-32.
  const { user } = useAuth();
  const resumen = useFinanceSummary(hayFinanzas(user) ? {
    from: aISODate(from ? new Date(from) : new Date(2020, 0, 1)),
    to: aISODate(to ? new Date(to) : new Date()),
    groupBy: 'category',
  } : null);

  if (error && expenses.length === 0) return <LoadError what="los gastos" error={error} />;
  if (isLoading || resumen.isLoading) return <div className="dashboard-stats__loading"><Spinner size="lg" /></div>;
  return <ExpensesStatsView expenses={expenses} resumen={resumen.summary} from={from} to={to} periodLabel={periodLabel} />;
}

/** La vista pura: recibe los gastos ya cargados. Separada para poder probarla con datos sintéticos. */
export function ExpensesStatsView({ expenses, resumen, from, to, periodLabel }: { expenses: Expense[]; resumen?: FinanceSummary | null } & ExpensesStatsPanelProps) {
  const stats = useMemo(() => {
    const { start, end } = resolveRange(from, to, expenses.map(e => e.incurredAt));
    const local = summarizeExpenses(expenses);
    // Con el resumen del servidor, el total y las categorías son los suyos
    // (los mismos que Finanzas y el XLSX). Sin él —falló la llamada, o el
    // panel se prueba con datos sintéticos— se cae al cálculo local.
    const summary = resumen ? { ...local, total: resumen.totals.expenses, count: resumen.totals.expenseCount } : local;
    const byCategory = resumen
      ? resumen.rows
        .filter(r => Number(r.expenses) > 0)
        .map(r => ({ key: r.key, label: r.label, value: Number(r.expenses) }))
        .sort((a, b) => b.value - a.value)
      : expensesByCategory(expenses);
    return {
      summary,
      byCategory,
      byVehicle: expensesByVehicle(expenses, 6),
      byDriver: expensesByDriver(expenses, 6),
      trend: dailyAmounts(expenses, start, end),
    };
  }, [expenses, resumen, from, to]);

  const { summary } = stats;
  const topCategory = stats.byCategory[0];

  return (
    <div className="dashboard-stats">
      <div className="dashboard-stats__tiles">
        <StatTile label="Gasto total" value={formatearCOP(summary.total)} hint={`${summary.count} gasto${summary.count === 1 ? '' : 's'}`} />
        <StatTile
          label="Gasto promedio"
          value={summary.average === null ? '—' : pesos(summary.average)}
          hint="por gasto registrado"
        />
        <StatTile
          label="Categoría mayor"
          value={topCategory?.label ?? '—'}
          hint={topCategory ? pesos(topCategory.value) : undefined}
        />
        <StatTile
          label="Sin recibo"
          value={summary.withoutReceipt}
          hint={summary.count > 0 ? `de ${summary.count}` : undefined}
        />
      </div>

      <section className="dashboard-page__charts">
        <ChartCard title={`Por categoría — ${periodLabel}`} icon={<Layers size={18} />} empty={summary.count === 0}>
          <CategoryDonut data={stats.byCategory} centerLabel="gastado" centerValue={pesosCompactos(Number(summary.total))} valueFormatter={pesos} />
        </ChartCard>
        <ChartCard title={`Gasto por día — ${periodLabel}`} icon={<TrendingUp size={18} />} empty={summary.count === 0}>
          <DailyTrendChart data={stats.trend} color="#F59E0B" seriesName="Gasto" valueFormatter={pesos} />
        </ChartCard>
        <ChartCard title="Vehículos con más gasto" icon={<Truck size={18} />} empty={stats.byVehicle.length === 0}>
          <RankedBarChart data={stats.byVehicle} seriesName="Gasto" valueFormatter={pesos} />
        </ChartCard>
        <ChartCard title="Conductores con más gasto" icon={<Users size={18} />} empty={stats.byDriver.length === 0}>
          <RankedBarChart data={stats.byDriver} seriesName="Gasto" valueFormatter={pesos} />
        </ChartCard>
      </section>

      <p className="dashboard-stats__note">
        <Receipt size={13} /> Los gastos sin vehículo (p. ej. viáticos) cuentan en el total y en la categoría, pero no en el ranking de vehículos.
      </p>
    </div>
  );
}
